/**
 * DynamicDashboard.jsx - Dashboard completamente dinamica da Blueprint
 * ZERO testi hardcoded - tutto viene da Supabase
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../App';
import { useDashboardConfig } from '../../hooks/useDashboardConfig';
import supabase from '../../lib/supabase';
import { getMaterialImageUrl, PLACEHOLDER_IMAGE } from '../../utils/materialImages';
import { 
  // Icons - mapped dynamically
  Home, FolderPlus, FolderKanban, Search, Camera, Sparkles, 
  Layers, Eye, CheckCircle2, Play, ArrowRight, Users, 
  TrendingUp, FileText, MessageSquare, UserPlus, BarChart3,
  Clock, ThumbsUp, ThumbsDown, HelpCircle, Plus, Star, Info
} from 'lucide-react';

// Icon mapping for dynamic rendering
const iconMap = {
  Home, FolderPlus, FolderKanban, Search, Camera, Sparkles,
  Layers, Eye, CheckCircle2, Play, ArrowRight, Users,
  TrendingUp, FileText, MessageSquare, UserPlus, BarChart3,
  Clock, ThumbsUp, ThumbsDown, HelpCircle, Plus, Star
};

// Get icon component by name
const getIcon = (iconName) => iconMap[iconName] || Layers;

// ========== CONTINUE WHERE YOU LEFT OFF SECTION ==========
const ContinueSection = ({ recentlyViewed, recentSearches }) => {
  const hasActivity = (recentlyViewed?.length > 0) || (recentSearches?.length > 0);
  
  if (!hasActivity) return null;
  
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-[var(--gold)]" />
        <h2 className="text-lg font-medium text-white">Continua da dove hai lasciato</h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Recent Visual Searches */}
        {recentSearches?.slice(0, 2).map((search) => (
          <Link
            key={search.id}
            to={search.top_result_id ? `/materials/${search.top_result_id}` : '/identify'}
            className="group bg-[#12121a] border border-white/10 rounded-xl p-4 hover:border-[var(--gold)]/50 transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[var(--gold)]/10 flex items-center justify-center shrink-0">
                <Search className="w-5 h-5 text-[var(--gold)]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/40 text-xs">Ricerca Visual</p>
                <p className="text-white font-medium truncate">{search.top_result_name || 'Ricerca salvata'}</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/30 text-xs">
                {new Date(search.created_at).toLocaleDateString('it-IT')}
              </span>
              {search.top_result_score && (
                <span className="text-[var(--gold)] text-xs font-medium">
                  {Math.round(search.top_result_score)}% match
                </span>
              )}
            </div>
          </Link>
        ))}
        
        {/* Recently Viewed Materials */}
        {recentlyViewed?.slice(0, 2).map((view) => (
          <Link
            key={view.id}
            to={`/materials/${view.material_id}`}
            className="group bg-[#12121a] border border-white/10 rounded-xl overflow-hidden hover:border-[var(--gold)]/50 transition-all"
          >
            <div className="aspect-[16/10] bg-[#1a1a2e] relative">
              <img 
                src={getMaterialImageUrl(view.materials)}
                alt={view.materials?.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
              />
              <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 rounded text-white/70 text-xs flex items-center gap-1">
                <Eye className="w-3 h-3" />
                Visto di recente
              </div>
            </div>
            <div className="p-3">
              <p className="text-white/40 text-xs mb-1">{view.materials?.category}</p>
              <p className="text-white font-medium truncate">{view.materials?.name}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

// ========== DYNAMIC COMPONENTS ==========

// Hero Section
const HeroSection = ({ hero, featurePills }) => {
  if (!hero || !hero.title) return null;
  
  const PrimaryCta = hero.cta_primary;
  const SecondaryCta = hero.cta_secondary;
  const PrimaryIcon = PrimaryCta?.icon ? getIcon(PrimaryCta.icon) : Play;
  
  return (
    <div className="grid grid-cols-12 gap-6 mb-8">
      {/* Left Content */}
      <div className="col-span-7 space-y-6">
        {/* Title */}
        <div>
          <p className="text-sm text-white/40 mb-2">{hero.subtitle?.split('.')[0] || ''}</p>
          <h1 className="text-4xl font-light text-white leading-tight">
            {hero.title}{' '}
            {hero.title_accent && (
              <span className="italic font-serif text-[#b08b5c]">{hero.title_accent}</span>
            )}{' '}
            {hero.title_suffix}
          </h1>
        </div>
        
        {/* Subtitle */}
        {hero.subtitle && (
          <p className="text-base text-white/50 leading-relaxed max-w-lg">
            {hero.subtitle}
          </p>
        )}
        
        {/* Feature Pills */}
        {featurePills && featurePills.length > 0 && (
          <div className="flex items-center gap-8 pt-2">
            {featurePills.map((pill, idx) => {
              const Icon = getIcon(pill.icon);
              return (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl border border-white/10 bg-white/[0.03] flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#b08b5c]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-white/90 font-medium">{pill.title}</p>
                    <p className="text-xs text-white/40">{pill.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Right - Hero Image */}
      <div className="col-span-5">
        {hero.hero_image && (
          <div className="relative rounded-2xl overflow-hidden h-64">
            <img 
              src={hero.hero_image}
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#0a0a0f]/50" />
            
            {/* CTA Button */}
            {PrimaryCta && (
              <Link 
                to={PrimaryCta.route || '#'}
                className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm hover:bg-white/20 transition-colors"
              >
                <PrimaryIcon className="w-4 h-4" fill="currentColor" />
                {PrimaryCta.label}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Action Card with optional tooltip
const ActionCard = ({ card }) => {
  const Icon = getIcon(card.icon);
  const [showTooltip, setShowTooltip] = React.useState(false);
  
  // Tooltip for Visual Match cards
  const isVisualMatch = card.title?.toLowerCase().includes('visual') || 
                        card.route?.includes('identify') ||
                        card.icon === 'Camera';
  const tooltipText = isVisualMatch ? 'Carica una foto di una lastra per trovare alternative simili' : null;
  
  return (
    <Link 
      to={card.route || '#'}
      className="group luxury-card overflow-hidden hover:luxury-glow-gold transition-all duration-500 relative"
      onMouseEnter={() => tooltipText && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Tooltip */}
      {showTooltip && tooltipText && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1a1a2e] border border-white/20 rounded-lg shadow-xl z-20 whitespace-nowrap">
          <div className="flex items-center gap-2 text-white/80 text-xs">
            <Info className="w-3 h-3 text-[var(--gold)]" />
            {tooltipText}
          </div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-6 border-transparent border-t-[#1a1a2e]" />
        </div>
      )}
      
      {/* Image */}
      {card.image && (
        <div className="relative h-32 overflow-hidden">
          <img 
            src={card.image} 
            alt={card.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent" />
          
          {/* Icon Badge */}
          <div className="absolute top-3 left-3">
            <div className="w-9 h-9 rounded-xl bg-[#0a0a0f]/80 backdrop-blur-sm border border-white/10 flex items-center justify-center">
              <Icon className="w-4 h-4 text-white/70" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="p-4">
        <h3 className="text-sm font-medium text-white mb-1">{card.title}</h3>
        <p className="text-xs text-white/40 leading-relaxed mb-3">{card.description}</p>
        <div className="flex items-center text-[#b08b5c] text-xs group-hover:text-[#c9a577] transition-colors">
          <span className="mr-1">{card.cta || 'Get started'}</span>
          <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
};

// Action Cards Section
const ActionCardsSection = ({ title, cards }) => {
  if (!cards || cards.length === 0) return null;
  
  return (
    <div className="mb-8">
      {title && <h2 className="text-lg font-light text-white mb-4">{title}</h2>}
      <div className="grid grid-cols-4 gap-4">
        {cards.map((card, idx) => (
          <ActionCard key={idx} card={card} />
        ))}
      </div>
    </div>
  );
};

// Checklist Item
const ChecklistItem = ({ item }) => (
  <div className="flex items-start gap-3 py-2">
    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
      item.completed 
        ? 'bg-emerald-500/20 text-emerald-400' 
        : 'border border-white/20 text-white/40'
    }`}>
      {item.completed ? (
        <CheckCircle2 className="w-4 h-4" />
      ) : (
        <span className="text-[10px] font-medium">{item.number}</span>
      )}
    </div>
    <div>
      <p className={`text-sm ${item.completed ? 'text-white/60' : 'text-white/90'}`}>{item.title}</p>
      <p className="text-xs text-white/40">{item.description}</p>
    </div>
  </div>
);

// Checklist Section
const ChecklistSection = ({ title, items }) => {
  if (!items || items.length === 0) return null;
  
  return (
    <div>
      {title && <h2 className="text-lg font-light text-white mb-4">{title}</h2>}
      <div className="luxury-card p-4">
        {items.map((item, idx) => (
          <ChecklistItem key={idx} item={item} />
        ))}
      </div>
    </div>
  );
};

// Stats Widget
const StatsWidget = ({ widget }) => {
  if (!widget.stats || widget.stats.length === 0) return null;
  
  return (
    <div className="grid grid-cols-4 gap-4 mb-8">
      {widget.stats.map((stat, idx) => {
        const Icon = getIcon(stat.icon);
        return (
          <div key={idx} className="luxury-stat">
            <div className="flex items-center gap-3 mb-2">
              <Icon className="w-5 h-5 text-[#b08b5c]" />
              <span className="text-xs text-white/40">{stat.label}</span>
            </div>
            <div className="luxury-stat-number">{stat.default_value || '0'}</div>
          </div>
        );
      })}
    </div>
  );
};

// Workspace Widget (with demo content for first-time users)
const WorkspaceWidget = ({ widget, isFirstTime = false }) => {
  const emptyState = widget.empty_state;
  const Icon = emptyState?.icon ? getIcon(emptyState.icon) : FolderPlus;
  
  // Demo projects for first-time users
  const demoProjects = [
    {
      name: 'Villa Contemporanea',
      description: '3 materiali selezionati',
      image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=300&fit=crop',
      status: 'demo'
    },
    {
      name: 'Appartamento Milano',
      description: '5 materiali selezionati',
      image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=400&h=300&fit=crop',
      status: 'demo'
    }
  ];
  
  // Show demo content for first-time users
  if (isFirstTime) {
    return (
      <div>
        <h2 className="text-lg font-light text-white mb-4">{widget.title}</h2>
        <div className="space-y-3">
          {demoProjects.map((project, idx) => (
            <Link 
              key={idx}
              to="/projects"
              className="group luxury-card p-3 flex items-center gap-4 hover:luxury-glow-gold transition-all duration-300"
            >
              <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                <img 
                  src={project.image} 
                  alt={project.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-white/90 font-medium truncate">{project.name}</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#b08b5c]/20 text-[#b08b5c] uppercase tracking-wider">Demo</span>
                </div>
                <p className="text-xs text-white/40 mt-0.5">{project.description}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-[#b08b5c] transition-colors" />
            </Link>
          ))}
          
          {/* Create new project CTA */}
          <Link 
            to="/projects"
            className="flex items-center justify-center gap-2 py-4 border border-dashed border-white/20 rounded-xl text-white/40 hover:text-white/70 hover:border-white/30 transition-all"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm">Crea il tuo primo progetto</span>
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h2 className="text-lg font-light text-white mb-4">{widget.title}</h2>
      <div className="luxury-card p-6 text-center">
        <div className="w-16 h-16 rounded-2xl border border-white/10 bg-white/[0.02] flex items-center justify-center mx-auto mb-4">
          <Icon className="w-8 h-8 text-white/20" strokeWidth={1} />
        </div>
        <p className="text-sm text-white/60 mb-1">{emptyState?.title || 'No items yet'}</p>
        <p className="text-xs text-white/40 mb-4">{emptyState?.description || ''}</p>
        {emptyState?.cta && (
          <Link to={emptyState.route || '#'} className="luxury-btn-primary text-sm px-6 py-2.5 inline-block">
            {emptyState.cta}
          </Link>
        )}
      </div>
    </div>
  );
};

// Recent Materials Widget
const RecentMaterialsWidget = ({ widget }) => {
  // TODO: Fetch actual materials from source
  const mockMaterials = [
    { image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=400&fit=crop', name: 'Patagonia Green', type: 'Quartzite' },
    { image: 'https://images.unsplash.com/photo-1600585152220-90363fe7e115?w=300&h=400&fit=crop', name: 'Taj Mahal', type: 'Quartzite' },
    { image: 'https://images.unsplash.com/photo-1604147706283-d7119b5b822c?w=300&h=400&fit=crop', name: 'Cristallo Gold', type: 'Quartzite' },
    { image: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=300&h=400&fit=crop', name: 'Verde Alpi', type: 'Marble' },
  ];
  
  const materials = mockMaterials.slice(0, widget.limit || 4);
  
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-light text-white">{widget.title}</h2>
        <Link to="/materials" className="text-xs text-[#b08b5c] hover:text-[#c9a577] transition-colors flex items-center gap-1">
          View all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {materials.map((material, idx) => (
          <Link key={idx} to="/materials" className="group">
            <div className="aspect-[4/5] rounded-xl overflow-hidden luxury-card mb-2">
              <img 
                src={material.image} 
                alt={material.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <p className="text-xs text-white/80 font-medium truncate">{material.name}</p>
            <p className="text-[10px] text-white/40">{material.type}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

// Inspiration Widget
const InspirationWidget = ({ widget }) => {
  const content = widget.content;
  if (!content) return null;
  
  return (
    <div>
      <h2 className="text-lg font-light text-white mb-4">{widget.title}</h2>
      <div className="luxury-card overflow-hidden">
        <p className="text-xs text-white/50 p-4 pb-2">{content.text}</p>
        {content.image && (
          <div className="relative h-36">
            <img 
              src={content.image}
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent" />
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
              <span className="text-sm font-medium text-white tracking-wider">{content.label}</span>
              <ArrowRight className="w-4 h-4 text-[#b08b5c]" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Widget Renderer
const WidgetRenderer = ({ widget, isFirstTime = false }) => {
  if (!widget.enabled) return null;
  
  switch (widget.type) {
    case 'stats':
      return <StatsWidget widget={widget} />;
    case 'workspace':
      return <WorkspaceWidget widget={widget} isFirstTime={isFirstTime} />;
    case 'recent_materials':
      return <RecentMaterialsWidget widget={widget} />;
    case 'inspiration':
      return <InspirationWidget widget={widget} />;
    case 'leads_list':
    case 'activity_feed':
    case 'projects_list':
    case 'pending_reviews':
      // TODO: Implement these widgets when data sources are ready
      return null;
    default:
      return null;
  }
};

// Widgets Grid Section
const WidgetsSection = ({ widgets, isFirstTime = false }) => {
  if (!widgets || widgets.length === 0) return null;
  
  // Sort by order
  const sortedWidgets = [...widgets].sort((a, b) => (a.order || 0) - (b.order || 0));
  
  // Group widgets for layout
  const statsWidget = sortedWidgets.find(w => w.type === 'stats');
  const otherWidgets = sortedWidgets.filter(w => w.type !== 'stats');
  
  return (
    <>
      {/* Stats row */}
      {statsWidget && <WidgetRenderer widget={statsWidget} isFirstTime={isFirstTime} />}
      
      {/* Other widgets in grid */}
      <div className="grid grid-cols-12 gap-6">
        {otherWidgets.map((widget, idx) => {
          // Determine column span based on widget type
          let colSpan = 'col-span-4';
          if (widget.type === 'workspace') colSpan = 'col-span-4';
          if (widget.type === 'recent_materials') colSpan = 'col-span-5';
          if (widget.type === 'inspiration') colSpan = 'col-span-3';
          
          return (
            <div key={idx} className={colSpan}>
              <WidgetRenderer widget={widget} isFirstTime={isFirstTime} />
            </div>
          );
        })}
      </div>
    </>
  );
};

// ========== MAIN COMPONENT ==========

// Config Missing Message - Clear error, not silent fallback
const ConfigMissingMessage = () => {
  return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-6">
        <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4">
          <HelpCircle className="w-8 h-8 text-amber-400" />
        </div>
        <h2 className="text-xl font-light text-white mb-2">Dashboard non configurata</h2>
        <p className="text-white/50 text-sm mb-4">
          La configurazione della dashboard non è stata trovata per il tuo ruolo professionale.
        </p>
        <p className="text-white/40 text-xs mb-6">
          Contatta l'amministratore o configura la dashboard nel Blueprint CMS.
        </p>
        <div className="flex gap-3 justify-center">
          <Link 
            to="/materials"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#b08b5c] text-black font-medium rounded-lg hover:bg-[#c9a577] transition-colors text-sm"
          >
            <Search className="w-4 h-4" />
            Esplora Materiali
          </Link>
          <Link 
            to="/workspace"
            className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/5 transition-colors text-sm"
          >
            <Layers className="w-4 h-4" />
            Workspace
          </Link>
        </div>
      </div>
    </div>
  );
};

const DynamicDashboard = () => {
  const { profile, user } = useAuth();
  const { config, loading, error } = useDashboardConfig();
  
  // Welcome message state
  const [showWelcome, setShowWelcome] = React.useState(true);
  const [isFirstLogin, setIsFirstLogin] = React.useState(false);
  
  // Recent activity state
  const [recentSearches, setRecentSearches] = React.useState([]);
  const [recentlyViewed, setRecentlyViewed] = React.useState([]);
  
  // Check if first login
  React.useEffect(() => {
    if (profile) {
      const onboardingCompleted = profile.onboarding_completed;
      const updatedAt = profile.updated_at ? new Date(profile.updated_at) : null;
      const now = new Date();
      const twoMinutesAgo = new Date(now.getTime() - 2 * 60 * 1000);
      setIsFirstLogin(onboardingCompleted && updatedAt && updatedAt > twoMinutesAgo);
    }
  }, [profile]);
  
  // Fetch recent activity from Supabase
  React.useEffect(() => {
    const fetchRecentActivity = async () => {
      if (!user?.id || !supabase) return;
      
      try {
        // Try to fetch recent visual searches
        try {
          const { data: searches } = await supabase
            .from('visual_searches')
            .select('id, created_at, query_image_url, top_result_name, top_result_id, top_result_score')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(3);
          
          if (searches) setRecentSearches(searches);
        } catch (e) {
          // Table doesn't exist yet - that's ok
        }
        
        // Try to fetch recently viewed materials
        try {
          const { data: viewed } = await supabase
            .from('material_views')
            .select(`
              id,
              created_at,
              material_id,
              materials (
                id,
                name,
                category,
                image_url,
                image_main
              )
            `)
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(4);
          
          if (viewed) setRecentlyViewed(viewed.filter(v => v.materials));
        } catch (e) {
          // Table doesn't exist yet - that's ok
        }
      } catch (err) {
        console.error('Dashboard: Failed to fetch recent activity', err);
      }
    };
    
    fetchRecentActivity();
  }, [user?.id]);
  
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#b08b5c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/40 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error && !config) {
    return (
      <div className="luxury-card p-8 text-center">
        <p className="text-red-400 mb-2">Error loading dashboard configuration</p>
        <p className="text-white/40 text-sm">{error.message}</p>
      </div>
    );
  }
  
  // No config state - show clear message
  if (!config) {
    return <ConfigMissingMessage />;
  }
  
  const firstName = profile?.first_name || profile?.full_name?.split(' ')[0] || 'utente';
  
  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      {showWelcome && profile && (
        <div className="relative p-4 rounded-xl bg-gradient-to-r from-[var(--gold)]/10 to-transparent border border-[var(--gold)]/20">
          <button 
            onClick={() => setShowWelcome(false)}
            className="absolute top-3 right-3 text-white/40 hover:text-white/60 text-xl leading-none"
          >
            ×
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[var(--gold)]/20 flex items-center justify-center">
              {isFirstLogin ? (
                <Sparkles className="w-6 h-6 text-[var(--gold)]" />
              ) : (
                <Home className="w-6 h-6 text-[var(--gold)]" />
              )}
            </div>
            
            <div>
              <h3 className="text-white font-medium text-lg">
                {isFirstLogin ? (
                  <>Benvenuto in <span className="text-[var(--gold)]">SlabReality</span>, {firstName}!</>
                ) : (
                  <>Bentornato, <span className="text-[var(--gold)]">{firstName}</span>!</>
                )}
              </h3>
              <p className="text-white/50 text-sm">
                {isFirstLogin 
                  ? 'La tua dashboard è pronta. Inizia esplorando i materiali o creando il tuo primo progetto.'
                  : 'Riprendi da dove ti eri fermato. Ecco le tue attività recenti.'
                }
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Continue Where You Left Off */}
      <ContinueSection 
        recentlyViewed={recentlyViewed} 
        recentSearches={recentSearches} 
      />
      
      {/* Hero Section */}
      <HeroSection 
        hero={config.hero} 
        featurePills={config.feature_pills} 
      />
      
      {/* Action Cards + Checklist Row */}
      <div className="grid grid-cols-12 gap-6">
        {/* Action Cards */}
        <div className="col-span-8">
          <ActionCardsSection 
            title="Start something new" 
            cards={config.action_cards} 
          />
        </div>
        
        {/* Checklist */}
        <div className="col-span-4">
          <ChecklistSection 
            title="Getting started" 
            items={config.checklist_items} 
          />
        </div>
      </div>
      
      {/* Widgets */}
      <WidgetsSection widgets={config.widgets} isFirstTime={isFirstLogin} />
    </div>
  );
};

export default DynamicDashboard;
